import { HonorificEnum } from "../../../common/enums/PatientEnums";
import { getCountriesArray, US_STATES } from "../../../common/function/getCountriesArr";
import {
  ClinicalInfoInterface,
  DemographicInfoInterface,
} from "../../../types/PatientData.interface";
import { FormBuilderInterface } from "../../common/FormBuilder";
import { ReactComponent as SearchIcon } from "../../../common/assats/table/search.svg";

export interface AddPatientFieldConfigInterface extends FormBuilderInterface {
  name: keyof (ClinicalInfoInterface & DemographicInfoInterface);
}

export const demographicsAddPatientFieldConfig: AddPatientFieldConfigInterface[] =
  [
    {
      name: "honorific",
      type: "dropdownWithText",
      label: "patientsList.addPatient.demographics.honorific",
      optionsArray: Object.values(HonorificEnum),
      required: true,
      translateLabel: true,
    },
    {
      name: "gender",
      type: "dropdownWithText",
      label: "patientsList.addPatient.demographics.gender",
      optionsArray: [
        "patientsList.addPatient.demographics.genderOptions.male",
        "patientsList.addPatient.demographics.genderOptions.female",
        "patientsList.addPatient.demographics.genderOptions.other",
      ],
      translateOptions: true,
      required: true,
      translateLabel: true,
    },
    {
      name: "birthday",
      type: "date",
      label: "patientsList.addPatient.demographics.dateOfBirth",
      required: true,
      translateLabel: true,
    },
    {
      name: "firstName",
      type: "text",
      label: "patientsList.addPatient.demographics.firstName",
      required: true,
      translateLabel: true,
    },
    {
      name: "middleName",
      type: "text",
      label: "patientsList.addPatient.demographics.middleName",
      translateLabel: true,
    },
    {
      name: "lastName",
      type: "text",
      label: "patientsList.addPatient.demographics.lastName",
      translateLabel: true,
      required: true,
    },
    {
      name: "email",
      type: "text",
      label: "patientsList.addPatient.demographics.email",
      translateLabel: true,
      required: true,
    },
    {
      name: "phoneNumber",
      type: "phone",
      label: "patientsList.addPatient.demographics.phoneNumber",
      translateLabel: true,
      required: true,
    },
    {
      name: "maritalStatus",
      type: "dropdownWithText",
      label: "patientsList.addPatient.demographics.maritalStatus",
      optionsArray: [
        "patientsList.addPatient.demographics.maritalStatusOptions.single",
        "patientsList.addPatient.demographics.maritalStatusOptions.married",
        "patientsList.addPatient.demographics.maritalStatusOptions.divorced",
        "patientsList.addPatient.demographics.maritalStatusOptions.widowed",
        "patientsList.addPatient.demographics.maritalStatusOptions.other",
      ],
      translateOptions: true,
      required: true,
      translateLabel: true,
    },
    {
      name: "race",
      type: "dropdownWithText",
      label: "patientsList.addPatient.demographics.race",
      optionsArray: [
        "patientsList.addPatient.demographics.raceOptions.americanIndianAlaskaNative",
        "patientsList.addPatient.demographics.raceOptions.asian",
        "patientsList.addPatient.demographics.raceOptions.blackAfricanAmerican",
        "patientsList.addPatient.demographics.raceOptions.hispanicLatino",
        "patientsList.addPatient.demographics.raceOptions.nativeHawaiianOtherPacificIslander",
        "patientsList.addPatient.demographics.raceOptions.white",
        "patientsList.addPatient.demographics.raceOptions.other",
      ],
      translateOptions: true,
      translateLabel: true,
      required: true,
    },
    {
      name: "identityNumber",
      type: "text",
      label: "patientsList.addPatient.demographics.patientId",
      translateLabel: true,
      required: true,
    },
  ];

const secondClinical: AddPatientFieldConfigInterface[] = [
  {
    name: "smokingStatus",
    type: "dropdown",
    label: "patientsList.addPatient.secondClinical.smokingStatus",
    optionsArray: [
      "patientsList.addPatient.secondClinical.smokingStatusOptions.cessationOver5yrs",
      "patientsList.addPatient.secondClinical.smokingStatusOptions.cessationLessThan5yrs",
      "patientsList.addPatient.secondClinical.smokingStatusOptions.yesOverPackDay",
      "patientsList.addPatient.secondClinical.smokingStatusOptions.yesLessPackDay",
      "patientsList.addPatient.secondClinical.smokingStatusOptions.no",
    ],
    translateOptions: true,
    translateLabel: true,
    required: true,
  },
  {
    name: "takingOtherDrugs",
    type: "multiSelect",
    label: "patientsList.addPatient.secondClinical.takingOtherDrugs",
    optionsArray: [
      "patientsList.addPatient.secondClinical.prescriptionMedicationsOptions.statins",
      "patientsList.addPatient.secondClinical.prescriptionMedicationsOptions.bpDrugs",
    ],
    translateOptions: true,
    translateLabel: true,
    required: true,
  },
  {
    name: "sugarLevel",
    type: "text",
    label: "patientsList.addPatient.secondClinical.sugarLevel",
    translateLabel: true,
    suffix: "mg/dL",
    required: true,
  },
  {
    name: "totalCholesterol",
    type: "text",
    label: "patientsList.addPatient.secondClinical.totalCholesterol",
    translateLabel: true,
    suffix: "mg/dL",
    required: true,
  },
  {
    name: "ldl",
    type: "text",
    label: "patientsList.addPatient.secondClinical.ldl",
    translateLabel: true,
    suffix: "mg/dL",
    required: true,
  },
  {
    name: "triglycerides",
    type: "text",
    label: "patientsList.addPatient.secondClinical.triglycerides",
    translateLabel: true,
    suffix: "mg/dL",
    required: true,
  },
  {
    name: "bpSystolic",
    type: "text",
    label: "patientsList.addPatient.secondClinical.bpSystolic",
    translateLabel: true,
    suffix: "mmHg",
    required: true,
  },
  {
    name: "bpDiastolic",
    type: "text",
    label: "patientsList.addPatient.secondClinical.bpDiastolic",
    translateLabel: true,
    required: true,
    suffix: "mmHg",
  },
  {
    name: "height",
    type: "text",
    label: "patientsList.addPatient.secondClinical.height",
    translateLabel: true,
    required: true,
    checkMetric:true,
    metric:'metric'
  },
  {
    name: "heightInch",
    type: "text",
    label: "patientsList.addPatient.secondClinical.heightInch",
    translateLabel: true,
    required: true,
    checkMetric:true,
    metric:'imperial'
  },
  {
    name: "heightFeet",
    type: "text",
    label: "patientsList.addPatient.secondClinical.heightFeet",
    translateLabel: true,
    required: true,
    checkMetric:true,
    metric:'imperial'
  },
  {
    name: "weight",
    type: "text",
    label: "patientsList.addPatient.secondClinical.weight",
    translateLabel: true,
    translateOffset: false,
    required: true,
    checkMetric:true,
    metric:'metric'
  },
  {
    name: "weightLbs",
    type: "text",
    label: "patientsList.addPatient.secondClinical.weightLbs",
    translateLabel: true,
    translateOffset: false,
    required: true,
    checkMetric:true,
    metric:'imperial'
  },
  
  {
    name: "bmi",
    type: "info",
    label: "patientsList.addPatient.secondClinical.bmi",
    translateLabel: true,
  },
  {
    name: "notes",
    type: "textarea",
    label: "patientsList.addPatient.secondClinical.notes",
    translateLabel: true,
    fullRow: true,
  },
];

const thirdClinical: AddPatientFieldConfigInterface[] = [
  {
    name: "copd",
    question: "patientsList.addPatient.thirdClinical.copd",
    optionsArray: [
      "patientsList.addPatient.thirdClinical.options.yes",
      "patientsList.addPatient.thirdClinical.options.no",
    ],
    type: "radio",
    fullRow: true,
    translateLabel: true,
    translateOptions: true,
    required: true,
  },
  {
    name: "arrhythmiasOrConductionAbnormality",
    question:
      "patientsList.addPatient.thirdClinical.arrhythmiasOrConductionAbnormality",
    optionsArray: [
      "patientsList.addPatient.thirdClinical.options.yes",
      "patientsList.addPatient.thirdClinical.options.no",
    ],
    type: "radio",
    fullRow: true,
    translateLabel: true,
    translateOptions: true,
    required: true,
  },
  {
    name: "peripheralArterialDisease",
    question: "patientsList.addPatient.thirdClinical.peripheralArterialDisease",
    optionsArray: [
      "patientsList.addPatient.thirdClinical.options.yes",
      "patientsList.addPatient.thirdClinical.options.no",
    ],
    type: "radio",
    fullRow: true,
    translateLabel: true,
    translateOptions: true,
    required: true,
  },
  {
    name: "familyHistoryCardiovascularEvents",
    question:
      "patientsList.addPatient.thirdClinical.familyHistoryCardiovascularEvents",
    optionsArray: [
      "patientsList.addPatient.thirdClinical.options.yes",
      "patientsList.addPatient.thirdClinical.options.no",
    ],
    type: "radio",
    fullRow: true,
    translateLabel: true,
    translateOptions: true,
    required: true,
  },
  {
    name: "cerebrovascularDisease",
    question: "patientsList.addPatient.thirdClinical.cerebrovascularDisease",
    optionsArray: [
      "patientsList.addPatient.thirdClinical.options.yes",
      "patientsList.addPatient.thirdClinical.options.no",
    ],
    type: "radio",
    fullRow: true,
    translateLabel: true,
    translateOptions: true,
    required: true,
  },
  {
    name: "strokeOrTiaOrRIND",
    question: "patientsList.addPatient.thirdClinical.strokeOrTiaOrRIND",
    optionsArray: [
      "patientsList.addPatient.thirdClinical.options.yes",
      "patientsList.addPatient.thirdClinical.options.no",
    ],
    type: "radio",
    fullRow: true,
    translateLabel: true,
    translateOptions: true,
    required: true,
  },
  {
    name: "chronicHeartFailure",
    question: "patientsList.addPatient.thirdClinical.chronicHeartFailure",
    optionsArray: [
      "patientsList.addPatient.thirdClinical.options.yes",
      "patientsList.addPatient.thirdClinical.options.no",
    ],
    type: "radio",
    fullRow: true,
    translateLabel: true,
    translateOptions: true,
    required: true,
  },
];

const fourthClinical: AddPatientFieldConfigInterface[] = [
  {
    name: "lvefLessThan30",
    question: "patientsList.addPatient.fourthClinical.lvefLessThan30",
    optionsArray: [
      "patientsList.addPatient.fourthClinical.options.yes",
      "patientsList.addPatient.fourthClinical.options.no",
    ],
    type: "radio",
    fullRow: true,
    translateLabel: true,
    translateOptions: true,
    required: true,
  },
  {
    name: "previousCardiacEvent",
    question: "patientsList.addPatient.fourthClinical.previousCardiacEvent",
    optionsArray: [
      "patientsList.addPatient.fourthClinical.options.yes",
      "patientsList.addPatient.fourthClinical.options.no",
    ],
    type: "radio",
    fullRow: true,
    translateLabel: true,
    translateOptions: true,
    required: true,
  },
  {
    name: "hemodynamicInstability",
    question: "patientsList.addPatient.fourthClinical.hemodynamicInstability",
    optionsArray: [
      "patientsList.addPatient.fourthClinical.options.yes",
      "patientsList.addPatient.fourthClinical.options.no",
    ],
    type: "radio",
    fullRow: true,
    translateLabel: true,
    translateOptions: true,
    required: true,
  },
  {
    name: "pulmonaryEdema",
    question: "patientsList.addPatient.fourthClinical.pulmonaryEdema",
    optionsArray: [
      "patientsList.addPatient.fourthClinical.options.yes",
      "patientsList.addPatient.fourthClinical.options.no",
    ],
    type: "radio",
    fullRow: true,
    translateLabel: true,
    translateOptions: true,
    required: true,
  },
  {
    name: "anemia",
    question: "patientsList.addPatient.fourthClinical.anemia",
    optionsArray: [
      "patientsList.addPatient.fourthClinical.options.yes",
      "patientsList.addPatient.fourthClinical.options.no",
    ],
    type: "radio",
    fullRow: true,
    translateLabel: true,
    translateOptions: true,
    required: true,
  },
  {
    name: "otherCoMorbidities",
    label: "patientsList.addPatient.fourthClinical.otherCoMorbidities",
    type: "text",
    colWidth: "col-12",
    fullRow: true,
    translateLabel: true,
  },
];

const addressFields: AddPatientFieldConfigInterface[] = [
  {
    name: "address",
    type: "text",
    label: "Address (street name and house number)",
    colWidth: "12",
    required: true,
  },
  { name: "city", type: "text", label: "City", required: true },
  // NOTE - THE STATE LOCATION IS REQUIRED TO BE INDEX 2. if changed see the addPatientFrom.tsx file and fix useEffect accordingly!!!!@!
  {
    name: "state",
    type: "text",
    label: "State",
    // optionsArray: US_STATES.map((state) => state.name),
    icon: <SearchIcon />,
  },
  { name: "zip", type: "text", label: "Zipcode" },
  {
    name: "country",
    type: "autoComplete",
    optionsArray: getCountriesArray(true) as string[],
    label: "Country",
    required: true,
    icon: <SearchIcon />,
  },
];

const firstClinicalConditional: AddPatientFieldConfigInterface[] = [
  {
    name: "isSymptomatic",
    question: "Is the patient symptomatic?",
    optionsArray: ["Yes", "No"],
    type: "radio",
    fullRow: true,
    required: true,
  },
];

export const firstClinicalConditionalTrue: AddPatientFieldConfigInterface[] = [
  {
    name: "symptomsType",
    question: "Type of symptoms",
    optionsArray: ["Acute", "Chronic"],
    type: "radio",
    fullRow: true,
    required: true,
  },
  {
    name: "chestPain",
    question: "Chest pain",
    optionsArray: ["Yes", "No"],
    type: "radio",
    fullRow: true,
    required: true,
  },
  {
    name: "shortnessOfBreath",
    question: "Shortness of breath",
    optionsArray: ["Yes", "No"],
    type: "radio",
    fullRow: true,
    required: true,
  },
  {
    name: "shoulderOrArmPain",
    question: "Shoulder or arm pain",
    optionsArray: ["Yes", "No"],
    type: "radio",
    fullRow: true,
    required: true,
  },
  {
    name: "jawPain",
    question: "Jaw pain",
    optionsArray: ["Yes", "No"],
    type: "radio",
    fullRow: true,
    required: true,
  },
  {
    name: "dizzinessOrPresyncope",
    question: "Dizziness/pre-syncope",
    optionsArray: ["Yes", "No"],
    type: "radio",
    fullRow: true,
    required: true,
  },
  {
    name: "nausea",
    question: "Nausea",
    optionsArray: ["Yes", "No"],
    type: "radio",
    fullRow: true,
    required: true,
  },
];

const chestPainQuestions: AddPatientFieldConfigInterface[] = [
  {
    name: "chestPainType",
    question: "Type of chest pain",
    optionsArray: ["Typical", "Atypical"],
    type: "radio",
    nested: 1,
    fullRow: true,
    required: true,
  },
];


export const firstClinicalConditionalFunc = (
  isChestPain: boolean,
  isSymptomatic: boolean
): AddPatientFieldConfigInterface[] => {
  if (!isSymptomatic) {
    return []; // If not symptomatic, return an empty array
  }

  const questions: AddPatientFieldConfigInterface[] = [
    {
      name: "symptomsType",
      question: "Type of symptoms",
      optionsArray: ["Acute", "Chronic"],
      type: "radio",
      fullRow: true,
      required: true,
    },
    {
      name: "chestPain",
      question: "Chest pain",
      optionsArray: ["Yes", "No"],
      type: "radio",
      fullRow: true,
      required: true,
    },
    ...(isChestPain
      ? [
        ...chestPainQuestions, // Include chest pain type questions if chest pain is true
      ]
      : []),
    {
      name: "shortnessOfBreath",
      question: "Shortness of breath",
      optionsArray: ["Yes", "No"],
      type: "radio",
      fullRow: true,
      required: true,
    },
    {
      name: "shoulderOrArmPain",
      question: "Shoulder or arm pain",
      optionsArray: ["Yes", "No"],
      type: "radio",
      fullRow: true,
      required: true,
    },
    {
      name: "jawPain",
      question: "Jaw pain",
      optionsArray: ["Yes", "No"],
      type: "radio",
      fullRow: true,
      required: true,
    },
    {
      name: "dizzinessOrPresyncope",
      question: "Dizziness/pre-syncope",
      optionsArray: ["Yes", "No"],
      type: "radio",
      fullRow: true,
      required: true,
    },
    {
      name: "nausea",
      question: "Nausea",
      optionsArray: ["Yes", "No"],
      type: "radio",
      fullRow: true,
      required: true,
    },
  ];

  return questions;
};

export const AddPatientFieldConfig = {
  DEMOGRAPHICS: demographicsAddPatientFieldConfig,
  ADDRESS: addressFields,
  CLINICAL_ONE: firstClinicalConditional,
  CLINICAL_TWO: secondClinical,
  CLINICAL_THREE: thirdClinical,
  CLINICAL_FOUR: fourthClinical,
};
